import axios from "axios";
import Vue from "vue";
import { Plugins, Capacitor, Device } from "@capacitor/core";
const { Storage } = Plugins;
import { BASE_URL } from "@/constants/constants";

const BASE_SUBSCRIPTION_URL = `${BASE_URL}/account/application/2/subscription`;

export default {
  state: {
    subscriptions: [],
    active_subscription: {},
    building: {},
  },

  actions: {
    clearSubscriptionsData({ commit }) {
      return new Promise((resolve) => {
        commit("setSubscriptions", []);
        commit("setActiveSubscription", {});
        commit("setBuilding", {});
        Storage.clear().then(() => {
          resolve();
        });
      });
    },

    fetchSubscriptions({ commit, dispatch }, headers) {
      return new Promise((resolve, reject) => {
        axios
          .get(BASE_SUBSCRIPTION_URL, { headers: headers })
          .then((response) => {
            commit("setSubscriptions", response.data);
            commit("setAuthorization", headers.Authorization, { root: true });
            dispatch("registerPushToken");
            resolve(response);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },

    registerPushToken({ rootState, getters }) {
      const token = getters.getFCMToken;
      const headers = { Authorization: rootState.authorization };
      if (token !== false) {
        const data = {
          type: Capacitor.getPlatform(),
          registration_id: token,
          device_id: Device.getUid(),
          active: true,
        };
        axios
          .post(`${BASE_URL}/account/devices/`, data, { headers: headers })
          .then((response) => {
            return response;
          });
      }
    },

    async fetchBuildingProfile({ commit, state, rootState, dispatch }) {
      const headers = { Authorization: rootState.authorization };
      const sub = state.active_subscription;
      const buildingId = sub.building_id || sub.building || sub.id;
      let response = await axios.get(
        `${BASE_URL}/api/2.0/subscription/${sub.id}/building_profile?building_id=${buildingId}`,
        { headers: headers },
      );
      if (response.status === 200) {
        commit("setBuilding", response.data[0]);
        return Promise.resolve(response.data[0]);
      }
      return Promise.reject();
    },

    getSubscriptionData({ rootState, commit, dispatch }) {
      return new Promise((resolve, reject) => {
        Storage.get({ key: "subscriptions" }).then((response) => {
          let subscriptions = JSON.parse(response.value);
          if (subscriptions && subscriptions.length) {
            commit("setSubscriptions", subscriptions);
            dispatch("fetchSubscriptions", {
              Authorization: rootState.authorization,
            });
            Storage.get({ key: "active_subscription" }).then((response) => {
              commit("setActiveSubscription", JSON.parse(response.value));
              resolve();
            });
          } else {
            reject();
          }
        });
      });
    },

    async getSubscription({ commit }) {
      const subscription = await Storage.get({ key: "active_subscription" });
      commit("setActiveSubscription", JSON.parse(subscription.value));
      return subscription;
    },

    async createCustomMessage({ commit, state, rootState, dispatch }, payload) {
      const headers = {
        Authorization: rootState.authorization,
        "Content-Type": "application/json",
      };
      const buildingId =
        payload.building_id ||
        state.building.building_id ||
        state.building.id ||
        rootState.subscriptions.active_subscription.building_id ||
        rootState.subscriptions.active_subscription.building ||
        rootState.subscriptions.active_subscription.id;

      try {
        const response = await axios.post(
          `${BASE_URL}/api/2.0/predefined_messages/`,
          {
            message: payload.message,
            building_id: buildingId,
          },
          { headers: headers },
        );

        console.log("✅ Create API Response:", response.data);

        if (response.status === 200 && response.data.status === true) {
          // Refresh building profile to get updated messages
          return Promise.resolve(response.data);
        }

        return Promise.reject(response.data);
      } catch (error) {
        console.error("❌ Error creating message:", error);
        return Promise.reject(error);
      }
    },

    async updateCustomMessage({ commit, rootState, dispatch }, payload) {
      const headers = {
        Authorization: rootState.authorization,
        "Content-Type": "application/json",
      };

      try {
        const response = await axios.put(
          `${BASE_URL}/api/2.0/predefined_messages/`,
          {
            id: payload.id,
            message: payload.message,
          },
          { headers: headers },
        );

        console.log("✅ Update API Response:", response.data); // ✅ Debug

        // Just check for 200 status
        if (response.status === 200) {
          // Refresh building profile
          return Promise.resolve(response.data);
        }

        return Promise.reject(response.data);
      } catch (error) {
        console.error("❌ Error updating message:", error);
        return Promise.reject(error);
      }
    },

    async deleteCustomMessage({ commit, rootState, dispatch }, messageId) {
      const headers = {
        Authorization: rootState.authorization,
      };

      try {
        const response = await axios.delete(
          `${BASE_URL}/api/2.0/predefined_messages/${messageId}/`,
          { headers: headers },
        );

        console.log("✅ Delete API Response:", response.data); // ✅ Debug

        // Check if request was successful
        if (response.status === 200) {
          // Refresh building profile to get updated messages
          return Promise.resolve(response.data);
        }

        return Promise.reject(response.data);
      } catch (error) {
        console.error("❌ Error deleting message:", error);
        return Promise.reject(error);
      }
    },
  },

  mutations: {
    setSubscriptions(state, data) {
      Vue.set(state, "subscriptions", data);
      Storage.set({
        key: "subscriptions",
        value: JSON.stringify(data),
      });
    },

    setActiveSubscription(state, data) {
      Vue.set(state, "active_subscription", data);
      Storage.set({
        key: "active_subscription",
        value: JSON.stringify(data),
      });
    },

    setBuilding(state, data) {
      Vue.set(state, "building", data);
      Storage.set({
        key: "active_building",
        value: JSON.stringify(data),
      });
    },
  },

  getters: {
    getActiveSubscription(state) {
      return state.active_subscription;
    },

    getActiveBuilding(state) {
      return state.building;
    },

    getUserCan: (state) => (permission) => {
      return state.active_subscription;
    },

    getBuildingMessages(state) {
      return state.building?.messages || [];
    },

    getPresetMessages(state) {
      return (state.building.messages || []).filter(
        (msg) => msg.is_preset === true,
      );
    },

    getCustomMessages(state) {
      return (state.building.messages || []).filter(
        (msg) => msg.is_preset === false,
      );
    },
  },
};
